/* begin general import */
import { Table } from "antd";
import React from "react";
import nameof from "ts-nameof.macro";
/* end general import */

/* begin individual import */
import { UserRoleEnum } from "config/enum";
import { END_USER_MASTER_ROUTE, END_USER_ROUTE } from "config/route-consts";
import { Link } from "react-router-dom";
import { openOnRowClick } from "helpers/row-click";
import {
  DeviceDetailModal,
  useDeviceDetailModal,
} from "views/HomeControllerView/HomeControllerDetail/DeviceDetailModal";
import { deviceTypeMap } from "data/seeeds";
import { formatDate } from "helpers/date-time";
import { Device } from "models/Device";
import { EndUser } from "models/EndUser";
import { Scene } from "models/Scene";
import { endUserRepository } from "repositories/end-user-repository";
import { authService } from "services/auth-services";
import detailService from "services/pages/detail-service";
import userInactive from "./../../../assets/images/user/user-inactive.png";
import {
  ActivityList,
  ActivityModal,
  getActivitiesDict,
  useActivityModal,
} from "./ActivityModal";
import "./EndUserDetail.scss";
import { useEndUserDetailHook } from "./EndUserDetailHook";
import {
  ForgotPasswordModal,
  useForgotPasswordModal,
} from "./ForgotPasswordModal";
import { LockAccountModal, useLockAccoutModal } from "./LockAccountModal";
/* end individual import */

function EndUserPreview() {
  const { userRole } = authService.useRole();

  const { model, handleGoBase, handleUpdateNewModel, loadError } = detailService.useDetail(
    EndUser,
    endUserRepository.get,
    endUserRepository.save,
    END_USER_ROUTE
  );

  const {
    homeControllerColumns,
    dormitoryColumns,
    deviceColumns,
    sceneColumns,
    handleDeactiveAccount,
    handleActiveAccount,
  } = useEndUserDetailHook(model, handleUpdateNewModel, endUserRepository.save);

  const {
    visible: visibleDeviceDetailModal,
    selectedDevice,
    openDeviceDetailModal,
    closeDeviceDetailModal,
  } = useDeviceDetailModal();

  const dormitoryMap = new Map(
    model.dormitories?.map((dorm) => [dorm.id, dorm.name])
  );
  const roomMap = new Map(model.rooms?.map((r) => [r.id, r]));

  const deviceList: Device[] = model.devices?.map((d: Device) => {
    const joinType = deviceTypeMap.get(d?.deviceTypeId);
    const joinDormitory = dormitoryMap.get(d?.dormitoryId);
    return { ...d, typeName: joinType?.typeName, dormitoryName: joinDormitory };
  });

  const sceneList: Scene[] = model.scenes?.map((s) => {
    const joinRoom = roomMap.get(s.roomId);
    const joinDormitory = joinRoom
      ? dormitoryMap.get(joinRoom.dormitoryId)
      : undefined;
    return {
      ...s,
      roomName: joinRoom?.name,
      dormitoryId: joinRoom?.dormitoryId,
      dormitoryName: joinDormitory,
    };
  });

  const {
    visibleLockAccountModal,
    openLockAccountModal,
    cancelLockAccountModal,
    activeAccountModal,
    deactiveAccountmodal,
  } = useLockAccoutModal(handleDeactiveAccount, handleActiveAccount);

  const {
    visibleForgotPasswordModal,
    openForgotPasswordModal,
    cancelForgotPasswordModal,
    saveForgotPasswordModal,
  } = useForgotPasswordModal();

  const {
    loading,
    list,
    visibleActivityModal,
    openActivityModal,
    cancelActivityModal,
    handleApplyFilter,
  } = useActivityModal(endUserRepository.getActivityLog);

  const isActive = model.statusId === 1;

  // Show deleted account message
  if (loadError) {
    return (
      <>
        <div className="page end-user-detail">
          <div className="end-user-detail__deleted">
            <i className="tio-user_remove" />
            <h2>Tài khoản đã xóa</h2>
            <p>Tài khoản này đã bị xóa hoặc không tồn tại trong hệ thống.</p>
            <button className="end-user-detail__back-btn" onClick={handleGoBase}>
              <i className="tio-arrow_backward" />
              Quay lại danh sách
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page end-user-detail">
        <Link to={END_USER_MASTER_ROUTE} className="page-back">
          <i className="tio-chevron_left" aria-hidden />
          Danh sách người dùng
        </Link>
        {/* Profile Header */}
        <div className="end-user-detail__header">
          <div className="end-user-detail__avatar">
            <img src={model?.avatar ?? userInactive} alt={model.displayName} />
            <span
              className={`end-user-detail__avatar-status ${
                !isActive ? "end-user-detail__avatar-status--inactive" : ""
              }`}
            />
          </div>

          <div className="end-user-detail__info">
            <h1 className="end-user-detail__name">
              {model.displayName || "—"}
            </h1>
            <p className="end-user-detail__email">{model.email || model.username}</p>

            <div className="end-user-detail__meta">
              {model.phoneNumber && (
                <div className="end-user-detail__meta-item">
                  <i className="tio-call" />
                  <strong>{model.phoneNumber}</strong>
                </div>
              )}
              {model.sex?.name && (
                <div className="end-user-detail__meta-item">
                  <i className="tio-user" />
                  <span>{model.sex.name}</span>
                </div>
              )}
              {model.birthday && (
                <div className="end-user-detail__meta-item">
                  <i className="tio-calendar" />
                  <span>{formatDate(model.birthday)}</span>
                </div>
              )}
              <div
                className={`end-user-detail__status end-user-detail__status--${
                  isActive ? "active" : "inactive"
                }`}
              >
                {model.status?.name || (isActive ? "Hoạt động" : "Đã khóa")}
              </div>
            </div>
          </div>

          {userRole.includes(UserRoleEnum.Admin) && (
            <div className="end-user-detail__actions">
              <button
                className="end-user-detail__action-btn"
                onClick={openForgotPasswordModal}
              >
                <i className="tio-lock_outlined" />
                Đổi mật khẩu
              </button>
              <button
                className={`end-user-detail__action-btn ${
                  isActive ? "end-user-detail__action-btn--danger" : ""
                }`}
                onClick={openLockAccountModal}
              >
                <i className={isActive ? "tio-block" : "tio-checkmark_circle"} />
                {isActive ? "Khóa tài khoản" : "Mở tài khoản"}
              </button>
            </div>
          )}
        </div>

        {/* Stats Summary */}
        <div className="end-user-detail__stats">
          <div className="end-user-detail__stat-card">
            <div className="end-user-detail__stat-icon end-user-detail__stat-icon--house">
              <i className="tio-home_outlined" />
            </div>
            <div className="end-user-detail__stat-info">
              <span className="end-user-detail__stat-value">{model?.dormitories?.length || 0}</span>
              <span className="end-user-detail__stat-label">Nhà</span>
            </div>
          </div>
          <div className="end-user-detail__stat-card">
            <div className="end-user-detail__stat-icon end-user-detail__stat-icon--hc">
              <i className="tio-router" />
            </div>
            <div className="end-user-detail__stat-info">
              <span className="end-user-detail__stat-value">{model?.homeControllers?.length || 0}</span>
              <span className="end-user-detail__stat-label">Home Controller</span>
            </div>
          </div>
          <div className="end-user-detail__stat-card">
            <div className="end-user-detail__stat-icon end-user-detail__stat-icon--device">
              <i className="tio-devices_1" />
            </div>
            <div className="end-user-detail__stat-info">
              <span className="end-user-detail__stat-value">{model?.devices?.length || 0}</span>
              <span className="end-user-detail__stat-label">Thiết bị</span>
            </div>
          </div>
          <div className="end-user-detail__stat-card">
            <div className="end-user-detail__stat-icon end-user-detail__stat-icon--scene">
              <i className="tio-layers_outlined" />
            </div>
            <div className="end-user-detail__stat-info">
              <span className="end-user-detail__stat-value">{model?.scenes?.length || 0}</span>
              <span className="end-user-detail__stat-label">Cảnh</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="end-user-detail__content">
          <div className="end-user-detail__main">
            {/* Houses */}
            <div className="end-user-detail__section">
              <div className="end-user-detail__section-header">
                <h3>
                  <i className="tio-home_outlined" />
                  Danh sách nhà
                </h3>
                <span className="count">{model?.dormitories?.length || 0}</span>
              </div>
              <div className="end-user-detail__section-body">
                {model?.dormitories?.length > 0 ? (
                  <Table
                    rowKey={nameof(model.homes[0].id)}
                    columns={dormitoryColumns}
                    pagination={false}
                    dataSource={model.dormitories}
                    scroll={{ x: "max-content" }}
                  />
                ) : (
                  <div className="end-user-detail__section-body--empty">
                    <i className="tio-home_outlined" />
                    <p>Chưa có nhà nào</p>
                  </div>
                )}
              </div>
            </div>

            {/* Home Controllers */}
            <div className="end-user-detail__section">
              <div className="end-user-detail__section-header">
                <h3>
                  <i className="tio-router" />
                  Danh sách Home Controller
                </h3>
                <span className="count">
                  {model?.homeControllers?.length || 0}
                </span>
              </div>
              <div className="end-user-detail__section-body">
                {model?.homeControllers?.length > 0 ? (
                  <Table
                    rowKey={nameof(model.homeControllers[0].id)}
                    columns={homeControllerColumns}
                    pagination={false}
                    dataSource={model.homeControllers}
                    scroll={{ x: "max-content" }}
                  />
                ) : (
                  <div className="end-user-detail__section-body--empty">
                    <i className="tio-router" />
                    <p>Chưa có Home Controller nào</p>
                  </div>
                )}
              </div>
            </div>

            {/* Devices */}
            <div className="end-user-detail__section">
              <div className="end-user-detail__section-header">
                <h3>
                  <i className="tio-devices_1" />
                  Danh sách thiết bị
                </h3>
                <span className="count">{model?.devices?.length || 0}</span>
              </div>
              <div className="end-user-detail__section-body">
                {deviceList?.length > 0 ? (
                  <Table
                    rowKey={nameof(model.devices[0].id)}
                    columns={deviceColumns}
                    pagination={false}
                    dataSource={deviceList}
                    onRow={(device) => openOnRowClick(() => openDeviceDetailModal(device))}
                    scroll={{ x: "max-content" }}
                  />
                ) : (
                  <div className="end-user-detail__section-body--empty">
                    <i className="tio-devices_1" />
                    <p>Chưa có thiết bị nào</p>
                  </div>
                )}
              </div>
            </div>

            {/* Scenes */}
            <div className="end-user-detail__section">
              <div className="end-user-detail__section-header">
                <h3>
                  <i className="tio-layers_outlined" />
                  Danh sách cảnh
                </h3>
                <span className="count">{sceneList?.length || 0}</span>
              </div>
              <div className="end-user-detail__section-body">
                {sceneList?.length > 0 ? (
                  <Table
                    rowKey={nameof(model.scenes[0].id)}
                    columns={sceneColumns}
                    pagination={false}
                    dataSource={sceneList}
                    scroll={{ x: "max-content" }}
                  />
                ) : (
                  <div className="end-user-detail__section-body--empty">
                    <i className="tio-layers_outlined" />
                    <p>Chưa có cảnh nào</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="end-user-detail__section">
          <div className="end-user-detail__section-header">
            <h3>
              <i className="tio-history" />
              Hoạt động gần đây
            </h3>
            <span className="count">{model?.activityLogs?.length || 0}</span>
          </div>
          <div className="end-user-detail__activity">
            <ActivityList
              list={getActivitiesDict(model.activityLogs, 5, 10)}
            />
          </div>
          <div className="end-user-detail__view-all" onClick={openActivityModal}>
            Xem tất cả
          </div>
        </div>
      </div>

      <LockAccountModal
        statusId={model.statusId}
        visibleLockAccountModal={visibleLockAccountModal}
        cancelLockAccountModal={cancelLockAccountModal}
        activeAccountModal={activeAccountModal}
        deactiveAccountmodal={deactiveAccountmodal}
      />
      <ForgotPasswordModal
        model={model}
        visibleForgotPasswordModal={visibleForgotPasswordModal}
        cancelForgotPasswordModal={cancelForgotPasswordModal}
        saveForgotPasswordModal={saveForgotPasswordModal}
      />
      <DeviceDetailModal
        device={selectedDevice}
        visible={visibleDeviceDetailModal}
        onClose={closeDeviceDetailModal}
      />
      <ActivityModal
        model={list}
        loading={loading}
        visibleActivationModal={visibleActivityModal}
        cancelActivationModal={cancelActivityModal}
        onFilter={handleApplyFilter}
      />
    </>
  );
}

export default EndUserPreview;
